import { Pagination, Stack } from '@inflearn/ds-react-next';
import { useTranslation } from 'next-i18next';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import { type ReactElement, useEffect } from 'react';
import type {
  ClientApiV1CourseGiftsSentQueryString,
  FindSentCourseGiftResponse,
} from '../../../../../resources/@types/courseSchema';
import { GiftPageLayout } from '../../../../FSD/shared/ui/layout/GiftPageLayout';
import EmptyStates from '../../../../components/common/EmptyStates';
import { transformGiftStatus } from '../../../../components/pages/my/gifts/common/giftUtils';
import {
  SentGiftItem,
  type SentGiftItemProps,
} from '../../../../components/pages/my/gifts/sent/SentGiftItem';
import { COURSE_URL } from '../../../../constants/urls';
import { useCustomQueryParams } from '../../../../hooks/useCustomQueryParams';
import { useCourseGiftsSentQuery } from '../../../../queries/useCourseGiftsSentQuery';
import { eventNames } from '../../../../utils/GTM/eventNames';
import { useTrackMixpanelEvent } from '../../../../utils/GTM/useTrackMixpanelEvent';
import { formatDateToYYYYMDHHMM, formatDateToYYYYMMDD } from '../../../../utils/time';

export const getStaticProps = async ({ locale }: { locale: string }) => ({
  props: {
    ...(await serverSideTranslations(locale, ['common'])),
  },
  revalidate: 60,
});

const DEFAULT_QUERY_PARAMS: ClientApiV1CourseGiftsSentQueryString = {
  pageSize: 10,
  pageNumber: 1,
};

function MySentGiftsPage() {
  const { queryParams, push } = useCustomQueryParams<ClientApiV1CourseGiftsSentQueryString>({
    defaultValue: DEFAULT_QUERY_PARAMS,
    queryParamKeys: ['pageNumber', 'pageSize'],
  });
  const { data, isError, isLoading } = useCourseGiftsSentQuery({
    params: queryParams,
  });
  const { trackMixpanelEvent, isReady } = useTrackMixpanelEvent();

  const pageNumber = Number(queryParams.pageNumber);

  const isEmpty = data?.items.length === 0;
  const gifts = data?.items.map(transformGiftItem);
  const totalPage = data?.totalPage ?? 1;

  const isReadyForEvent = isReady && !isLoading && data;

  useEffect(() => {
    if (!isReadyForEvent) {
      return;
    }

    trackMixpanelEvent(
      eventNames.VIEW_SEND_BOX,
      {
        count: data.items.length,
      },
      {
        once: true,
      },
    );
  }, [isReadyForEvent]);

  return (
    <>
      <EmptyStates
        height={300}
        isError={isError}
        isEmpty={isEmpty}
        empty={{
          title: '아직 보낸 선물이 없어요',
        }}
      >
        <Stack gap={20}>
          {gifts?.map((item) => (
            <SentGiftItem key={item.id} item={item} />
          ))}
        </Stack>
      </EmptyStates>
      {totalPage === DEFAULT_QUERY_PARAMS.pageNumber ? null : (
        <Pagination
          value={pageNumber}
          total={totalPage}
          onChange={(page) => push({ pageNumber: page }, { scroll: true })}
          styles={{
            root: {
              marginTop: 56,
              marginBottom: 80,
              justifyContent: 'center',
            },
          }}
        />
      )}
    </>
  );
}

export type GiftItemResponseType = FindSentCourseGiftResponse['items'][number];

export const enum GiftStatus {
  PENDING = 'pending',
  ACCEPTED = 'accepted',
  REFUNDED = 'refunded',
}

function transformGiftItem(item: GiftItemResponseType): SentGiftItemProps {
  const status = transformGiftStatus(item.status);
  return {
    id: item.id,
    orderDate: formatDateToYYYYMMDD(item.createdAt),
    courseTitle: item.orderTitle,
    courseDetailURL: COURSE_URL.COURSE_DETAIL(item.courseSlug),
    recipientName: item.receiverName,
    refundDeadline: getRefundDeadline(item),
    amount: item.paymentPrice,
    status,
    receiptUrl: item.isReceiptAvailable ? item.receiptUrl : '',
    detailURL: COURSE_URL.GIFTS_SENT_DETAIL({ giftId: item.id }),
  };
}

function getRefundDeadline(item: GiftItemResponseType): string {
  // 이미 환불한 경우 환불기한 없음
  if (item.status === 'CANCELED') {
    return '';
  }

  if (item.isRefundable && item.refundLimitedAt) {
    return `${formatDateToYYYYMDHHMM(item.refundLimitedAt)} 까지`;
  }

  if (!item.isRefundable) {
    return '환불 불가';
  }
  return '';
}

function MySentGiftsPageLayout({
  children,
}: {
  children: ReactElement;
}) {
  const { t } = useTranslation();
  return (
    <GiftPageLayout title={t('메타.보낸선물함.제목')} description={t('메타.보낸선물함.설명')}>
      {children}
    </GiftPageLayout>
  );
}

MySentGiftsPage.getLayout = (page: ReactElement) => (
  <MySentGiftsPageLayout>{page}</MySentGiftsPageLayout>
);

export default MySentGiftsPage;
